import { useEffect, useRef, useState } from "react";
import RodeoLevel from "@/game/rodeolevel";
import { preloadAssets } from "@/game/assets";
import { AudioManager } from "@/game/audio";
import { useLang } from "@/i18n";
import { Volume2, VolumeX, Heart, Settings, ChevronUp, ChevronDown, ChevronsUp } from "lucide-react";
import { useAuth } from "@/auth";
import { api } from "@/lib/api";
import BonusPauseMenu from "@/components/BonusPauseMenu";
import RewardModal from "@/components/RewardModal";
import { stopTitleAudio } from "@/lib/audioBus";

const base = `${process.env.PUBLIC_URL || ""}/game-assets`;
const RODEO_SONG = "bebiendo-toda-la-noche.mp3";
const BEST_KEY = "nicabros_rodeo_best_coins";
function saveRodeoBest(coins) {
  try {
    const prev = parseInt(localStorage.getItem(BEST_KEY) || "0", 10) || 0;
    if (coins > prev) localStorage.setItem(BEST_KEY, String(coins));
  } catch (e) {}
}

export default function RodeoLevelScreen({ character, difficulty, onComplete, onGameOver, onExit }) {
  const { t } = useLang();
  const { user, refreshUser } = useAuth();
  const canvasRef = useRef(null);
  const gameRef = useRef(null);
  const audioRef = useRef(null);
  const songRef = useRef(null);

  const [phase, setPhase] = useState("loading"); // loading | countdown | running | won | over
  const [countNum, setCountNum] = useState(3);
  const [hud, setHud] = useState({ pct: 0, coins: 0, lives: difficulty?.lives || 3 });
  const [muted, setMuted] = useState(false);
  const [runId, setRunId] = useState(0);
  const [wonData, setWonData] = useState(null);
  const [saved, setSaved] = useState(false); // false | "ok" | "err" | "login"
  const [showBoard, setShowBoard] = useState(false);
  const [board, setBoard] = useState(null);
  const [paused, setPaused] = useState(false);
  const [reward, setReward] = useState(null);

  const openPause = () => { const g = gameRef.current; if (g) g.paused = true; songRef.current?.pause(); setPaused(true); };
  const resumePlay = () => { gameRef.current?.begin(); const s = songRef.current; if (s && !muted) s.play().catch(() => {}); setPaused(false); };

  useEffect(() => { stopTitleAudio(); }, []);

  useEffect(() => {
    let game;
    let cancelled = false;
    setPhase("loading");
    const rideId = (character?.spriteId || character?.id) + "_ride";
    const need = ["rodeo_bull", "rodeo_sky", "rodeo_ground", "rodeo_stands", "rodeo_stands_b", "rodeo_stands_c",
      "obs_hay", "obs_goat", "obs_chicken", "obs_fence", "obs_trough", "obs_horse", "obs_moto", "obs_mariachi",
      "boss_bull", "boss_ganadero", "boss_gigantona", "boss_payaso", "laborracha", "beer", "stampede_bull_a", "stampede_bull_b", "sun_cowboy", "chancleta", character?.id, rideId];
    preloadAssets(need).then((assets) => {
      if (cancelled) return;
      const audio = new AudioManager();
      audioRef.current = audio;
      game = new RodeoLevel(canvasRef.current, {
        character,
        difficulty,
        assets,
        audio,
        callbacks: {
          onProgress: (p) => setHud((h) => ({ ...h, pct: p.pct, coins: p.coins, lives: p.lives, boss: !!p.boss })),
          onCoins: (c) => setHud((h) => ({ ...h, coins: c })),
          onLives: (l) => setHud((h) => ({ ...h, lives: l })),
          onComplete: (r) => { songRef.current?.pause(); saveRodeoBest(r?.coins || 0); setWonData(r || { coins: 0 }); setPhase("won"); },
          onGameOver: (r) => { songRef.current?.pause(); saveRodeoBest(r?.coins || 0); setPhase("over"); },
        },
      });
      gameRef.current = game;
      game.start();
      setPhase("countdown");
    });
    return () => {
      cancelled = true;
      game?.destroy();
      audioRef.current?.destroy();
      songRef.current?.pause();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  useEffect(() => {
    if (phase !== "countdown") return;
    setCountNum(3);
    const tm = [];
    tm.push(setTimeout(() => setCountNum(2), 700));
    tm.push(setTimeout(() => setCountNum(1), 1400));
    tm.push(setTimeout(() => setCountNum("GO"), 2100));
    tm.push(setTimeout(() => {
      gameRef.current?.begin();
      const s = songRef.current;
      if (s && !muted) { s.currentTime = 0; s.volume = 0.55; s.play().catch(() => {}); }
      setPhase("running");
    }, 2700));
    return () => tm.forEach(clearTimeout);
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleMute = () => {
    const m = !muted; setMuted(m);
    audioRef.current?.setMuted(m);
    if (songRef.current) songRef.current.muted = m;
  };

  const tap = (name) => ({
    onPointerDown: (e) => { e.preventDefault(); gameRef.current?.setInput(name); },
    onContextMenu: (e) => e.preventDefault(),
    draggable: false,
    style: { touchAction: "none", userSelect: "none", WebkitUserSelect: "none", WebkitTouchCallout: "none", WebkitTapHighlightColor: "transparent" },
  });

  const buildCard = (coins) => new Promise((resolve) => {
    const cv = document.createElement("canvas"); cv.width = 1080; cv.height = 1080;
    const c = cv.getContext("2d");
    const g = c.createLinearGradient(0, 0, 0, 1080);
    g.addColorStop(0, "#0b2a4a"); g.addColorStop(1, "#7a1f0f");
    c.fillStyle = g; c.fillRect(0, 0, 1080, 1080);
    c.strokeStyle = "#ffd54a"; c.lineWidth = 14; c.strokeRect(30, 30, 1020, 1020);
    c.textAlign = "center";
    c.fillStyle = "#ffd54a"; c.font = "900 118px 'Baloo 2', system-ui"; c.fillText("¡CAMPEÓN!", 540, 210);
    c.fillStyle = "#fff"; c.font = "800 44px 'Baloo 2', system-ui"; c.fillText("Derroté al Jefe · Corrida de Toros", 540, 288);
    const drawRest = () => {
      c.fillStyle = "#ffe17a"; c.font = "900 84px 'Baloo 2', system-ui"; c.fillText(character?.name || "", 540, 830);
      c.fillStyle = "#fff"; c.font = "800 58px 'Baloo 2', system-ui"; c.fillText("C$ " + coins + " córdobas", 540, 910);
      c.fillStyle = "#ffd54a"; c.font = "900 52px 'Baloo 2', system-ui"; c.fillText("🐂 NICA BROS", 540, 998);
      resolve(cv);
    };
    const img = new Image(); img.crossOrigin = "anonymous";
    img.onload = () => { const w = 430, h = img.naturalHeight * (w / img.naturalWidth); c.drawImage(img, 540 - w / 2, 350, w, Math.min(440, h)); drawRest(); };
    img.onerror = drawRest;
    img.src = `${base}/${(character?.spriteId || character?.id)}_ride.webp`;
    setTimeout(() => { if (!img.complete) drawRest(); }, 1500);
  });

  const shareRun = async () => {
    const coins = wonData?.coins ?? hud.coins;
    const cv = await buildCard(coins);
    cv.toBlob(async (blob) => {
      if (!blob) return;
      const file = new File([blob], "nicabros-campeon.png", { type: "image/png" });
      const text = `¡Derroté al Jefe en Corrida de Toros con C$ ${coins}! 🐂 #NicaBros`;
      try {
        if (navigator.canShare && navigator.canShare({ files: [file] })) { await navigator.share({ title: "Nica Bros", text, files: [file] }); return; }
        if (navigator.share) { await navigator.share({ title: "Nica Bros", text }); return; }
      } catch (e) { return; }
      const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "nicabros-campeon.png"; a.click(); URL.revokeObjectURL(url);
    }, "image/png");
  };

  const submitScore = async () => {
    if (!user) { setSaved("login"); return; }
    const coins = wonData?.coins ?? hud.coins;
    try {
      await api.saveScore({
        player_name: (user.username || "ANON").slice(0, 16), character: character?.name || character?.id || "",
        level_index: 7, level_name: "Corrida de Toros", score: coins, cordobas: coins,
        completed: true, difficulty: difficulty?.id || difficulty?.key || "normal", mode: "corrida",
      });
      setSaved("ok");
    } catch (e) { setSaved("err"); }
  };

  const openBoard = async () => {
    setShowBoard(true); setBoard(null);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores?mode=corrida&sort=score&limit=10`);
      setBoard(await r.json());
    } catch (e) { setBoard([]); }
  };

  useEffect(() => {
    if (phase === "won" && wonData?.boss && user && saved === false) submitScore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, wonData, user]);

  // Beat the Corrida de Toros boss → unlock El Enano Cabezón free.
  useEffect(() => {
    if (phase === "won" && wonData?.boss && user) {
      api.claimLevelReward({ level_index: 7 }).then((r) => {
        if (r.data?.unlocked_character) { setReward(r.data.unlocked_character); refreshUser?.(); }
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, wonData, user]);

  const totalLives = difficulty?.lives || 3;

  return (
    <div className="relative w-full h-full bg-black overflow-hidden" style={{ touchAction: "none" }} data-testid="rodeo-level">
      <canvas id="rodeolevel-canvas" ref={canvasRef} width={960} height={540} />
      <audio ref={songRef} src={`${base}/${RODEO_SONG}`} loop preload="auto" />

      {/* HUD */}
      <div className="absolute top-0 left-0 w-full p-3 md:p-4 flex justify-between items-start z-40 pointer-events-none font-pixel pixel-stroke text-white">
        <div className="flex flex-col gap-1">
          <div className="text-[10px] md:text-sm text-[#FFEB3B]">{t("rodeoLevelName", "NIVEL RODEO")}</div>
          <div data-testid="rodeolevel-coins" className="text-base md:text-2xl text-white">C$ {hud.coins}</div>
          <div className="flex gap-1 mt-0.5" data-testid="rodeolevel-lives">
            {Array.from({ length: totalLives }).map((_, i) => (
              <Heart key={i} size={16} strokeWidth={2.5} fill={i < hud.lives ? "#FF1744" : "transparent"} className={i < hud.lives ? "text-[#FF1744]" : "text-white/40"} />
            ))}
          </div>
        </div>
        <div className="flex flex-col items-center gap-1">
          {hud.boss ? (
            <div className="text-[10px] md:text-sm text-[#FF1744] pixel-stroke">🐂 {t("rodeoBossLabel", "¡JEFE FINAL!")}</div>
          ) : (
            <>
              <div className="text-[8px] md:text-[10px] text-white/80">{t("rodeoLevelGoal", "¡LLEGA A LA META!")}</div>
              <div className="mt-1 w-40 md:w-64 h-3 rounded-full bg-black/40 overflow-hidden">
                <div className="h-full rounded-full bg-[#ff5722] transition-all duration-150" style={{ width: `${hud.pct}%` }} />
              </div>
            </>
          )}
        </div>
        <div className="pointer-events-auto flex gap-2">
          {phase === "running" && (
            <button data-testid="rodeolevel-settings-btn" onClick={openPause} className="pixel-btn bg-black/55 backdrop-blur text-white p-2.5">
              <Settings size={16} strokeWidth={2.5} />
            </button>
          )}
          <button data-testid="rodeolevel-mute-btn" onClick={toggleMute} className="pixel-btn bg-black/55 backdrop-blur text-white p-2.5">
            {muted ? <VolumeX size={16} strokeWidth={2.5} /> : <Volume2 size={16} strokeWidth={2.5} />}
          </button>
        </div>
      </div>

      <BonusPauseMenu open={paused} onResume={resumePlay} onExit={() => onExit && onExit()} muted={muted} onToggleSound={toggleMute} />

      {/* Touch controls: UP/DOWN lanes (left) + JUMP (right) */}
      {phase === "running" && (
        <div className="touch-only absolute bottom-6 left-0 w-full px-5 flex justify-between items-end z-40 select-none pointer-events-none">
          <div className="flex flex-col gap-4">
            <button {...tap("up")} className="pointer-events-auto pixel-btn bg-black/60 active:bg-[#0067C6] text-white w-28 h-24 opacity-95 flex items-center justify-center" data-testid="rodeolevel-up"><ChevronUp size={56} strokeWidth={3} className="pointer-events-none" /></button>
            <button {...tap("down")} className="pointer-events-auto pixel-btn bg-black/60 active:bg-[#0067C6] text-white w-28 h-24 opacity-95 flex items-center justify-center" data-testid="rodeolevel-down"><ChevronDown size={56} strokeWidth={3} className="pointer-events-none" /></button>
          </div>
          <button {...tap("jump")} className="pointer-events-auto pixel-btn bg-black/60 active:bg-[#4CAF50] text-white w-36 h-36 opacity-95 flex flex-col items-center justify-center gap-0.5" data-testid="rodeolevel-jump">
            <ChevronsUp size={56} strokeWidth={3} className="pointer-events-none" />
            <span className="font-pixel text-[9px] pointer-events-none">{t("rodeoJump", "SALTA")}</span>
          </button>
        </div>
      )}
      {phase === "running" && (
        <div className="hidden md:block absolute bottom-4 left-1/2 -translate-x-1/2 z-40 font-vt text-white/70 text-base pointer-events-none">
          ▲/▼ {t("rodeoMove", "MUÉVETE")} · ␣ {t("rodeoJump", "SALTA")}
        </div>
      )}

      {phase === "loading" && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black">
          <p className="font-pixel text-[#FFEB3B] text-sm blink">{t("loadingGame")}</p>
        </div>
      )}

      {phase === "countdown" && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-black/45" data-testid="rodeolevel-countdown">
          <div className="font-pixel text-[10px] md:text-sm text-[#FFEB3B] pixel-stroke mb-4">{t("rodeoLevelName", "NIVEL RODEO")}</div>
          <div key={String(countNum)} className="font-pixel pixel-stroke pop-in text-7xl md:text-9xl" style={{ color: countNum === "GO" ? "#4CAF50" : "#FF5722" }}>
            {countNum === "GO" ? t("bonusCountGo") : countNum}
          </div>
        </div>
      )}

      {phase === "won" && (
        <div className="absolute inset-0 z-[60] flex items-start sm:items-center justify-center bg-black/85 p-3 sm:p-4 overflow-y-auto scanlines" data-testid="rodeolevel-won">
          <div className={`pixel-card p-4 sm:p-6 md:p-8 w-full max-w-md text-center pop-in my-auto max-h-[96vh] overflow-y-auto ${wonData?.boss ? "bg-gradient-to-b from-[#3a1206] to-[#0b2440]" : "bg-[#141414]"}`}>
            {wonData?.boss ? (
              <>
                <div className="text-4xl mb-1">🏆🐂</div>
                <h2 className="font-pixel text-2xl md:text-4xl pixel-stroke text-[#FFD54A]">{t("rodeoChampion", "¡CAMPEÓN!")}</h2>
                <p className="font-vt text-lg text-white/90 mt-1">{t("rodeoBossBeaten", "¡Derrotaste al Jefe de la Corrida!")}</p>
              </>
            ) : (
              <h2 className="font-pixel text-2xl md:text-4xl pixel-stroke text-[#4CAF50]">{t("rodeoLevelDone", "¡LLEGASTE A LA META!")}</h2>
            )}
            <div className="my-3 py-3 border-y-2 border-white/15">
              <p className="font-pixel text-[10px] text-[#FFEB3B]">{t("cordobas", "CÓRDOBAS")}</p>
              <p className="font-pixel text-3xl sm:text-4xl md:text-5xl text-white mt-2">C$ {wonData?.coins ?? hud.coins}</p>
            </div>
            {wonData?.boss && (
              <div className="mb-3">
                {saved === "ok" && <p className="font-vt text-[#4CAF50] text-lg mb-1" data-testid="rodeo-saved-msg">🏆 {t("rodeoSaved", "¡Guardado en el ranking!")}</p>}
                {saved === "login" && <p className="font-vt text-[#FFD54A] text-sm mb-1" data-testid="rodeo-login-note">{t("rodeoLoginToSave", "Inicia sesión para guardar tu puntaje")}</p>}
                {saved === "err" && <p className="font-vt text-[#ff8a80] text-sm mb-1">{t("rodeoSaveErr", "No se pudo guardar, reintentá")}</p>}
                <div className="flex flex-col gap-2">
                  <button data-testid="rodeolevel-save-btn" onClick={submitScore} className="pixel-btn bg-[#2e7d32] text-white px-6 py-3 text-xs w-full">🏆 {t("rodeoSaveScore", "GUARDAR EN EL RANKING")}</button>
                  <button data-testid="rodeolevel-board-btn" onClick={openBoard} className="pixel-btn bg-[#0067C6] text-white px-6 py-3 text-xs w-full">🐂 {t("rodeoViewBoard", "VER RANKING")}</button>
                  <button data-testid="rodeolevel-share-btn" onClick={shareRun} className="pixel-btn bg-[#7a3ff2] text-white px-6 py-3 text-xs w-full">{t("rodeoShare", "📲 COMPARTIR MI HAZAÑA")}</button>
                </div>
              </div>
            )}
            <p className="font-vt text-xl text-white mb-3">{t("rodeoBonusPrompt", "¿Querés seguir al toro mecánico de bono?")}</p>
            <div className="flex flex-col gap-2">
              <button data-testid="rodeolevel-continue-bonus-btn" onClick={() => onComplete && onComplete(wonData)} className="pixel-btn bg-[#FF5722] text-white px-6 py-3 text-xs w-full">
                {t("rodeoGoBonus", "🐂 MONTAR EL TORO")}
              </button>
              <button data-testid="rodeolevel-skip-bonus-btn" onClick={() => onExit && onExit()} className="pixel-btn bg-[#222] text-white px-6 py-3 text-xs w-full">
                {t("rodeoSkipBonus", "NO, VOLVER AL MENÚ")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showBoard && (
        <div className="absolute inset-0 z-[70] flex items-center justify-center bg-black/90 p-4 scanlines" data-testid="rodeolevel-board">
          <div className="pixel-card bg-[#111] p-5 w-full max-w-md">
            <h3 className="font-pixel text-base md:text-lg text-[#FFEB3B] pixel-stroke text-center mb-3">🐂 {t("rodeoBoardTitle", "RANKING · CORRIDA DE TOROS")}</h3>
            {board === null ? (
              <p className="font-vt text-white text-center py-6">{t("loading", "Cargando...")}</p>
            ) : board.length === 0 ? (
              <p className="font-vt text-white/70 text-center py-6">{t("noScores", "¡Sé el primero!")}</p>
            ) : (
              <div className="space-y-1">
                {board.map((s, i) => (
                  <div key={s.id || i} className="grid grid-cols-12 font-vt text-lg items-center py-1" style={{ color: i === 0 ? "#FFEB3B" : i === 1 ? "#ddd" : i === 2 ? "#FF7043" : "#fff" }} data-testid={`rodeo-board-row-${i}`}>
                    <span className="col-span-2 font-pixel text-xs">{["🥇", "🥈", "🥉"][i] || i + 1}</span>
                    <span className="col-span-7 truncate">🇳🇮 {s.player_name} <span className="text-white/40 text-sm">· {s.character}</span></span>
                    <span className="col-span-3 text-right">C$ {s.score}</span>
                  </div>
                ))}
              </div>
            )}
            <button data-testid="rodeolevel-board-close" onClick={() => setShowBoard(false)} className="pixel-btn bg-[#FF5722] text-white px-6 py-3 text-xs w-full mt-4">{t("close", "CERRAR")}</button>
          </div>
        </div>
      )}

      {phase === "over" && (
        <div className="absolute inset-0 z-[60] flex items-center justify-center bg-black/85 p-4 scanlines" data-testid="rodeolevel-over">
          <div className="pixel-card bg-[#141414] p-6 md:p-10 w-full max-w-md text-center pop-in">
            <h2 className="font-pixel text-2xl md:text-4xl pixel-stroke text-[#FF1744]">{t("gameOver", "GAME OVER")}</h2>
            <div className="my-6 py-4 border-y-2 border-white/15">
              <p className="font-pixel text-[10px] text-[#FFEB3B]">{t("cordobas", "CÓRDOBAS")}</p>
              <p className="font-pixel text-4xl md:text-5xl text-white mt-2">C$ {hud.coins}</p>
            </div>
            <div className="flex flex-col gap-2">
              <button data-testid="rodeolevel-retry-btn" onClick={() => setRunId((n) => n + 1)} className="pixel-btn bg-[#FF5722] text-white px-6 py-3 text-xs w-full">
                {t("retry", "REINTENTAR")}
              </button>
              <button data-testid="rodeolevel-menu-btn" onClick={() => onExit && onExit()} className="pixel-btn bg-[#222] text-white px-6 py-3 text-xs w-full">
                {t("backToMenu", "MENÚ")}
              </button>
            </div>
          </div>
        </div>
      )}
      {reward && <RewardModal charId={reward.id} charName={reward.name} onClose={() => setReward(null)} />}
    </div>
  );
}
